/**
 * ModuleScopeBar — jednotný chrome: výběr nodu + odemčení layoutu.
 *
 * Zamčeno: jen ikona zámku (nerozbíjí lištu).
 * Odemčeno: zámek s popiskem „Hotovo“ + předané akce s popisky (Widget, Spona…).
 */
import type { ReactNode } from 'react';
import { Lock, Unlock } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { RefreshButton } from '../components/ui/RefreshButton';
import { useIsPhoneLayout } from '../lib/ui-layout';
import { cn } from '../lib/utils';
import { NodePicker } from './NodePicker';

export interface ModuleScopeBarProps {
  /** null = lokál */
  node: string | null;
  onNodeChange: (node: string | null) => void;
  /** Layout edit (widget panely). Bez prop = skryté. */
  editing?: boolean;
  onToggleEdit?: () => void;
  onRefresh?: () => void | Promise<void>;
  refreshing?: boolean;
  /** Extra akce vpravo (typicky jen když editing) */
  actions?: ReactNode;
  className?: string;
  /** Vždy viditelný při scrollu */
  sticky?: boolean;
  /** Skrýt popisek „Nod“ */
  compact?: boolean;
}

export function ModuleScopeBar({
  node,
  onNodeChange,
  editing,
  onToggleEdit,
  onRefresh,
  refreshing,
  actions,
  className,
  sticky = true,
  compact = false,
}: ModuleScopeBarProps) {
  const { t } = useTranslation();
  const phone = useIsPhoneLayout();
  const unlocked = !!editing;
  // Na telefonu vždy úsporný chrome (label „Nod“ zabírá místo)
  const isCompact = compact || phone;

  return (
    <div
      className={cn(
        'flex items-center gap-2 flex-wrap py-2',
        sticky &&
          'sticky top-0 z-20 -mx-0.5 px-0.5 bg-[rgb(var(--acm-shell)/0.92)] backdrop-blur-md border-b border-gray-800/50',
        className,
      )}
    >
      <div className='flex items-center gap-1.5 min-w-0'>
        {!isCompact && (
          <span className='text-[11px] text-gray-500 shrink-0'>{t('common.node', 'Nod')}</span>
        )}
        <div className={cn('min-w-0', isCompact ? 'w-28 sm:w-40' : 'w-36 sm:w-44')}>
          <NodePicker value={node} onChange={onNodeChange} />
        </div>
      </div>

      <div className='ml-auto flex items-center gap-1.5 flex-wrap justify-end'>
        {actions}
        {onRefresh && (
          <RefreshButton onRefresh={onRefresh} loading={!!refreshing} />
        )}
        {onToggleEdit && (
          unlocked
            ? (
              <Button
                variant='primary'
                size='sm'
                icon={<Unlock size={14} />}
                onClick={onToggleEdit}
                aria-pressed
              >
                {t('common.done', 'Hotovo')}
              </Button>
            )
            : (
              <Button
                variant='ghost'
                size='icon'
                icon={<Lock size={14} />}
                onClick={onToggleEdit}
                title={t('widgets.unlock', 'Odemknout')}
                aria-label={t('widgets.unlock', 'Odemknout')}
                aria-pressed={false}
              />
            )
        )}
      </div>
    </div>
  );
}
